import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { growthApi } from "@/lib/api";
import Loading from "@/components/app/Loading";
import { IDS } from "@/constants/testIds";
import { Lock } from "lucide-react";

function Bullets({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="text-sm space-y-1 list-disc list-inside" style={{ color: "var(--pa-text-primary)" }}>
      {items.map((s, i) => <li key={i}>{s}</li>)}
    </ul>
  );
}

export default function Dynamics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    growthApi.getDynamics()
      .then(setData)
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="pa-card p-6 text-center" data-testid={IDS.dynamicsPage}>
        <Lock size={22} color="var(--pa-gold)" className="mx-auto mb-3" />
        <h2 className="pa-serif text-xl mb-2" style={{ color: "var(--pa-text-primary)" }}>Dynamics unavailable</h2>
        <p className="text-sm mb-4" style={{ color: "var(--pa-text-secondary)" }}>{error.message}</p>
        <Link to="/unlock" className="pa-btn-outline">Enter Access Code</Link>
      </div>
    );
  }

  const combo = data?.combination;
  const others = data?.with_others || [];

  return (
    <div className="space-y-6" data-testid={IDS.dynamicsPage}>
      <header>
        <h1 className="pa-serif text-3xl md:text-4xl" style={{ color: "var(--pa-text-primary)" }}>Dynamics</h1>
        <p className="text-sm mt-1" style={{ color: "var(--pa-text-secondary)" }}>
          How your Primary and Secondary work together, and how the combination interacts with each of the other archetypes.
        </p>
      </header>

      {combo && (
        <section className="pa-card p-5 md:p-6">
          <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--pa-gold)" }}>
            {data?.primary}{data?.secondary ? ` + ${data.secondary}` : ""}
          </p>
          <h2 className="pa-serif text-xl md:text-2xl mb-3" style={{ color: "var(--pa-text-primary)" }}>
            {combo.title || "Your Combination"}
          </h2>
          <Bullets items={combo.items} />
        </section>
      )}

      {others.length > 0 && (
        <section className="space-y-3">
          <h2 className="pa-serif text-xl" style={{ color: "var(--pa-gold)" }}>With other archetypes</h2>
          {others.map((o) => (
            <div key={o.archetype} className="pa-card p-4 md:p-5">
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <h3 className="pa-serif text-lg" style={{ color: "var(--pa-text-primary)" }}>{o.archetype}</h3>
                {o.level && (
                  <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--pa-gold)" }}>
                    {o.level}
                  </span>
                )}
              </div>
              {o.tagline && (
                <p className="text-xs md:text-sm mb-2" style={{ color: "var(--pa-text-secondary)" }}>
                  {o.tagline}
                </p>
              )}
              <Bullets items={o.items} />
            </div>
          ))}
        </section>
      )}

      {!combo && others.length === 0 && (
        <div className="pa-card p-6 text-center">
          <p className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>
            No dynamics available yet. Complete the assessment to see how your Primary and Secondary combine.
          </p>
        </div>
      )}
    </div>
  );
}
